// Runtime UI translation: swaps English text nodes/attributes for the chosen language's dictionary.
export const LANGUAGES = [
  { code: "en", label: "English" },
  { code: "es", label: "Español" },
  { code: "pt", label: "Português" },
  { code: "fr", label: "Français" },
  { code: "zh", label: "中文 (简体)" },
  { code: "vi", label: "Tiếng Việt" },
];
const LOADERS = {
  es: () => import("../i18n/es.json"),
  pt: () => import("../i18n/pt.json"),
  fr: () => import("../i18n/fr.json"),
  zh: () => import("../i18n/zh.json"),
  vi: () => import("../i18n/vi.json"),
};
const ATTRS = ["placeholder", "title", "aria-label"];
// Hand-corrected terms where the generated dictionary picked the wrong sense
const OVERRIDES = {
  zh: { Items: "物料", Item: "物料", Open: "未结", "Stock Items": "库存物料" },
  vi: { Open: "Đang mở" },
};
const SKIP = "script,style,textarea,code,pre,[contenteditable=true],[data-no-translate]";
const EDGE = /^([\s·•\-–—,.:;()]*)(.*?)([\s·•\-–—,.:;()]*)$/s;

let dict = null;
let current = "en";
let observer = null;
const listeners = new Set();

const lookup = (src) => {
  if (!dict || !src) return src;
  const norm = src.replace(/\s+/g, " ");
  if (dict[norm.trim()]) return norm.replace(norm.trim(), dict[norm.trim()]);
  const m = norm.match(EDGE);
  if (m && m[2] && dict[m[2]]) return `${m[1]}${dict[m[2]]}${m[3]}`;
  return src;
};

const skipped = (el) => !el || (el.closest && el.closest(SKIP));

function doText(node) {
  if (skipped(node.parentElement)) return;
  if (node.nodeValue !== node.__i18nOut) node.__i18nSrc = node.nodeValue;
  const out = lookup(node.__i18nSrc);
  node.__i18nOut = out;
  if (node.nodeValue !== out) node.nodeValue = out;
}

function doAttrs(el) {
  if (skipped(el)) return;
  const store = (el.__i18nAttr = el.__i18nAttr || {});
  for (const a of ATTRS) {
    const v = el.getAttribute(a);
    if (v == null) continue;
    const rec = store[a] || (store[a] = {});
    if (v !== rec.out) rec.src = v;
    rec.out = lookup(rec.src);
    if (v !== rec.out) el.setAttribute(a, rec.out);
  }
}

function doTree(root) {
  if (root.nodeType === 3) return doText(root);
  if (root.nodeType !== 1) return;
  doAttrs(root);
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT);
  for (let n = walker.nextNode(); n; n = walker.nextNode()) {
    if (n.nodeType === 3) doText(n);
    else doAttrs(n);
  }
}

function start() {
  if (observer) return;
  observer = new MutationObserver((muts) => {
    for (const m of muts) {
      if (m.type === "characterData") doText(m.target);
      else if (m.type === "attributes") doAttrs(m.target);
      else m.addedNodes.forEach(doTree);
    }
  });
  observer.observe(document.body, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ATTRS });
}

let seq = 0;

export async function setLanguage(code) {
  const mine = ++seq;
  const next = LOADERS[code] ? code : "en";
  const loaded = next === "en" ? null : { ...(await LOADERS[next]()).default, ...(OVERRIDES[next] || {}) };
  if (mine !== seq) return;
  dict = loaded;
  current = next;
  localStorage.setItem("lang", next);
  document.documentElement.lang = next;
  start();
  doTree(document.body);
  listeners.forEach((fn) => fn(next));
}

export const getLanguage = () => current;
export const savedLanguage = () => localStorage.getItem("lang") || "en";
export const onLanguageChange = (fn) => { listeners.add(fn); return () => listeners.delete(fn); };
export const t = (s) => lookup(s);
